<template>
  <div :data-test="'determination-question-row-' + questionKey">
    <div class="row">
      <div class="col-sm-8">
        <span
          class="f-500"
          data-test="determination-question"
        >
          {{ question.title ? question.title : question.text }}
        </span>
        &nbsp;
        <span
          v-if="question.help"
          v-tooltip="question.help ? question.help : 'No help for this item'"
          class="c-indigo-500"
        >
          <i class="zmdi zmdi-help" />
        </span>
        <br />
        <span v-if="question.title && question.text" class="small c-gray">
          {{ question.text }}
        </span>
        <slot name="notice" />
      </div>
      <div class="col-sm-3">
        <span
          v-if="readOnly"
          :class="answerValue ? 'f-500' : 'c-gray'"
          data-test="determination-answer-readonly"
        >
          <template v-if="answerIs('yes')">{{ $t('Yes') }}</template>
          <template v-else-if="answerIs('no')">{{ $t('No') }}</template>
          <template v-else-if="answerIs('unknown')">{{ $t("Don't know") }}</template>
          <template v-else>{{ $t('Pending') }}</template>
        </span>
        <div v-else class="row">
          <div class="col-sm-3">
            <a
              :class="{
                'bgm-green-400 green c-white ': answerIs('yes'),
                'bgm-truegray-400 c-gray': !answerIs('yes'),
              }"
              href="#"
              class="label label-default f-12"
              data-toggle="tooltip"
              data-test="determination-answer-yes"
              @click="toggleAnswer('yes')"
            >
              {{ $t('Yes') }}
            </a>
          </div>
          <div class="col-sm-3">
            <a
              :class="{
                'bgm-green-400 green c-white ': answerIs('no'),
                'bgm-truegray-400 c-gray': !answerIs('no'),
              }"
              href="#"
              class="label label-default f-12"
              data-test="determination-answer-no"
              data-toggle="tooltip"
              @click="toggleAnswer('no')"
            >
              {{ $t('No') }}
            </a>
          </div>
          <div class="col-sm-6">
            <a
              v-tooltip="!answerIs('unknown') ? $t('Click, if you do not know the answer.') : ''"
              :class="{
                'bgm-orange c-white ': answerIs('unknown'),
                'bgm-truegray-400 c-gray faded': !answerIs('unknown'),
              }"
              href="#"
              class="label label-default f-11"
              data-test="determination-answer-unknown"
              data-toggle="tooltip"
              @click="toggleAnswer('unknown')"
            >
              {{ $t("Don't know") }}
            </a>
          </div>
          <input
            v-model="answerValue"
            :name="questionKeyPrefix + '[' + questionKey + ']'"
            data-toggle="tooltip"
            type="hidden"
          />
        </div>
      </div>
    </div>
    <div v-if="showExplanation" class="row m-t-5">
      <div class="col-sm-11">
        <div>
          <a
            href="#"
            class="small c-gray"
            :aria-expanded="explanationOpen ? 'true' : 'false'"
            :aria-controls="'explanation-' + questionKey"
            data-test="determination-explanation-toggle"
            @click.prevent="explanationOpen = !explanationOpen"
          >
            {{ $t('Why?') }}
            <i :class="explanationOpen ? 'zmdi zmdi-chevron-up' : 'zmdi zmdi-chevron-down'" />
          </a>
        </div>
        <div
          v-if="readOnly"
          v-show="explanationOpen"
          :id="'explanation-' + questionKey"
          class="small c-gray f-italic m-t-5"
          data-test="determination-explanation-readonly"
        >
          {{ explanationValue }}
        </div>
        <template v-else>
          <div v-show="explanationOpen" class="small c-gray m-t-5 m-b-5">
            {{ $t('Optional. A short note on why you answered this way is saved with your answers.') }}
          </div>
          <textarea
            v-show="explanationOpen"
            :id="'explanation-' + questionKey"
            v-model="explanationValue"
            :name="explanationKeyPrefix + '[' + questionKey + ']'"
            :placeholder="$t('Explain your answer')"
            class="form-control"
            rows="2"
            maxlength="2000"
            data-test="determination-explanation"
          />
        </template>
      </div>
    </div>
    <hr class="m-t-15 m-b-15" />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'

export default {
  name: 'DeterminationSetting',
  props: {
    answer: { type: [String, Boolean], default: null },
    questionKey: { type: String, required: true },
    question: { type: Object, required: true },
    questionKeyPrefix: {
      type: String,
      required: false,
      default: 'determination_questions',
    },
    readOnly: { type: Boolean, default: false },
    explanation: { type: String, default: '' },
    explanationKeyPrefix: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const answerValue = ref(props.answer)
    const explanationValue = ref(props.explanation)

    // Most answers need no reason, so the box stays out of the way until asked for.
    // Text that is already there opens itself, so nothing written is hidden.
    const explanationOpen = ref(Boolean(props.explanation))

    watch(
      () => props.answer,
      (newAnswer) => {
        if (!newAnswer) return

        answerValue.value = newAnswer
      },
    )

    watch(
      () => props.explanation,
      (newExplanation) => {
        explanationValue.value = newExplanation

        if (newExplanation) {
          explanationOpen.value = true
        }
      },
    )

    // Someone reading another actor's answers is shown the reason only where one
    // was given; an empty row tells them nothing.
    const showExplanation = computed(() =>
      props.readOnly ? Boolean(explanationValue.value) : Boolean(props.explanationKeyPrefix),
    )

    return {
      answerValue,
      explanationValue,
      explanationOpen,
      showExplanation,
      answerIs: (answer) => answerValue.value === answer,
      toggleAnswer: (answer) => {
        if (props.readOnly) return

        answerValue.value = answer
        emit('changed', {
          questionKey: props.questionKey,
          answer: answerValue.value,
        })
      },
    }
  },
}
</script>
